import { memo, useDeferredValue, useEffect, useMemo, useRef, type RefObject } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronDown, ChevronUp, Copy, ExternalLink, Link2, Link2Off, MoreHorizontal, Settings, X } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipTrigger,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  cn,
} from "@nexus/ui";
import type { TenantListItem, AccessGroupItem } from "@/features/identity-hub/api/identity-hub.api";
import { humanizeLabel, orgColor } from "@/features/identity-hub/utils/iam.utils";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { OrgLogoMark } from "./OrgLogoMark";
import { SuperTenantBadge } from "./SuperTenantBadge";
import { industryLabel } from "./OrgCard";
import { relativeTimeI18n } from "@/features/identity-hub/utils/time";
import { useTranslation } from "react-i18next";

// ─── Shared types ────────────────────────────────────────────────────
//
// Only server-sortable columns are exposed as sort headers. The tenants
// list API orders by `IamTenantSortBy = "name" | "createdAt" | "memberCount"`,
// so we never sort a client-derived key over partially-loaded pages.

export type OrgSortKey = "name" | "members" | "createdAt";

export interface OrgSortState {
  key: OrgSortKey;
  dir: "asc" | "desc";
}

// ─── Sub-components ───────────────────────────────────────────────────

function SortHeader({
  id, label, sort, onSort, className,
}: {
  id: OrgSortKey;
  label: string;
  sort: OrgSortState;
  onSort: (k: OrgSortKey) => void;
  className?: string;
}) {
  const active = sort.key === id;
  return (
    <th
      className={cn("whitespace-nowrap px-3 py-2.5 text-left", className)}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(id)}
        className="inline-flex cursor-pointer select-none items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {label}
        {active
          ? sort.dir === "asc"
            ? <ChevronDown className="h-3 w-3" />
            : <ChevronUp className="h-3 w-3" />
          : <ChevronDown className="h-3 w-3 opacity-0 group-hover:opacity-40" />}
      </button>
    </th>
  );
}

function RetoolCell({ retoolUserId }: { retoolUserId?: string | null }) {
  const { t } = useTranslation();
  const value = retoolUserId?.trim();
  const hasRetool = !!value;
  const label = hasRetool
    ? t("iam.orgsTable.retoolCell.hasId", "Retool ID: {{value}}", { value })
    : t("iam.orgsTable.retoolCell.noId", "No Retool ID");
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex h-5 w-5 items-center justify-center rounded-md",
            hasRetool ? "text-primary/75" : "text-muted-foreground/45",
          )}
          aria-label={label}
        >
          {hasRetool ? <Link2 className="h-3.5 w-3.5" /> : <Link2Off className="h-3.5 w-3.5" />}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

// Boolean Yes/No cell — green check for true, muted X for false. Mirrors
// RetoolCell's icon-in-a-box treatment; the aria-label is the accessible name
// the Orgs table story's play function selects on.
function BooleanCell({ value, yesLabel, noLabel }: { value: boolean; yesLabel: string; noLabel: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 w-5 items-center justify-center rounded-md",
        value ? "text-success" : "text-muted-foreground",
      )}
      aria-label={value ? yesLabel : noLabel}
    >
      {value ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
    </span>
  );
}

// ─── Derived row type ─────────────────────────────────────────────────

interface OrgRow {
  id: string;
  name: string;
  type: string;
  logoUrl?: string | null;
  brandColor?: string | null;
  retoolUserId?: string | null;
  createdAt: string;
  memberCount: number;
  roleCount: number;
  isSuperTenant: boolean;
  isArchived: boolean;
  industryCount: number;
  hasProducts: boolean;
  defaultBrand: string | null;
  defaultIndustry: string | null;
}

function deriveRows(
  tenants: TenantListItem[],
  membersByTenant: Map<string, unknown[]>,
  groupsByTenant: Map<string, AccessGroupItem[]>,
  superTenantIds: Set<string>,
): OrgRow[] {
  return tenants.map((t) => ({
    id: t.id,
    name: t.name,
    type: t.type,
    logoUrl: t.logoUrl,
    brandColor: t.brandColor,
    retoolUserId: t.retoolUserId,
    createdAt: t.createdAt,
    // Match OrgDrawer's "active roles only" view — groupsByTenant can include
    // archived rows for super tenants, which would inflate this count.
    memberCount: t.memberCount ?? membersByTenant.get(t.id)?.length ?? 0,
    roleCount: (groupsByTenant.get(t.id) ?? []).filter((r) => !r.deletedAt).length,
    isSuperTenant: superTenantIds.has(t.id),
    isArchived: !!t.deletedAt,
    industryCount: t.industryIndices.length,
    hasProducts: !!t.hasProducts,
    defaultBrand: t.defaultBrand ?? null,
    defaultIndustry: t.defaultIndustry ?? null,
  }));
}

// ─── OrgRow (memoized) ────────────────────────────────────────────────
//
// Primitive props + stable id-based callbacks let React.memo skip rows
// whose data/active state is unchanged — the same reconciliation win
// PeopleTable relies on across many Radix menu/tooltip roots.

interface OrgRowProps {
  row: OrgRow;
  isActive: boolean;
  onRowClick?: (id: string) => void;
  onOpenSettings?: (tenantId: string) => void;
  onOpenAuditLog?: (tenantId: string, name: string) => void;
}

function OrgRowImpl({ row, isActive, onRowClick, onOpenSettings, onOpenAuditLog }: OrgRowProps) {
  const { t } = useTranslation();
  const accent = row.brandColor ?? orgColor(row.name);
  return (
    <tr
      data-rowid={row.id}
      onClick={() => onRowClick?.(row.id)}
      className={cn(
        "group cursor-pointer transition-colors",
        isActive ? "bg-primary/5 hover:bg-primary/5" : "hover:bg-muted/40",
        row.isArchived && "opacity-70",
      )}
    >
      {/* Organization */}
      <td className="w-56 max-w-[224px] overflow-hidden px-3 py-3">
        <div className="flex items-center gap-2">
          <ImagePreviewTrigger src={row.logoUrl} alt={row.name}>
            <OrgLogoMark
              name={row.name}
              logoUrl={row.logoUrl}
              accent={accent}
              isSuperTenant={row.isSuperTenant}
              size={28}
              textClassName="text-[11px]"
            />
          </ImagePreviewTrigger>
          <span className="truncate font-medium text-foreground">{row.name}</span>
          {row.isSuperTenant && <SuperTenantBadge />}
          {row.isArchived && (
            <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {t("iam.shared.archivedBadge", "Archived")}
            </span>
          )}
        </div>
      </td>

      {/* Type */}
      <td className="hidden w-24 whitespace-nowrap px-3 py-3 md:table-cell">
        <span className="text-xs text-muted-foreground">{industryLabel(row.type)}</span>
      </td>

      {/* Members */}
      <td className="px-3 py-3">
        <span className="text-sm tabular-nums text-foreground">{row.memberCount}</span>
      </td>

      {/* Roles */}
      <td className="hidden px-3 py-3 sm:table-cell">
        <span className="text-sm tabular-nums text-foreground">{row.roleCount}</span>
      </td>

      {/* Industries (assigned count) */}
      <td className="hidden px-3 py-3 lg:table-cell">
        <span
          className="text-sm tabular-nums text-foreground"
          aria-label={t("iam.orgsTable.industryCount", {
            count: row.industryCount,
            defaultValue_one: "{{count}} industry",
            defaultValue_other: "{{count}} industries",
          })}
        >
          {row.industryCount}
        </span>
      </td>

      {/* Products (has any) */}
      <td className="hidden px-3 py-3 lg:table-cell">
        <BooleanCell
          value={row.hasProducts}
          yesLabel={t("iam.orgsTable.boolean.yes", "Yes")}
          noLabel={t("iam.orgsTable.boolean.no", "No")}
        />
      </td>

      {/* Default brand (normalized name, humanized for display) */}
      <td className="hidden max-w-[160px] px-3 py-3 xl:table-cell">
        <span className="block truncate text-xs text-muted-foreground">
          {row.defaultBrand ? humanizeLabel(row.defaultBrand) : t("iam.orgsTable.empty", "—")}
        </span>
      </td>

      {/* Default industry (normalized name, humanized for display) */}
      <td className="hidden max-w-[160px] px-3 py-3 xl:table-cell">
        <span className="block truncate text-xs text-muted-foreground">
          {row.defaultIndustry ? humanizeLabel(row.defaultIndustry) : t("iam.orgsTable.empty", "—")}
        </span>
      </td>

      {/* Retool */}
      <td className="hidden px-3 py-3 lg:table-cell">
        <RetoolCell retoolUserId={row.retoolUserId} />
      </td>

      {/* Created */}
      <td className="hidden px-3 py-3 lg:table-cell">
        <span className="text-xs tabular-nums text-muted-foreground">
          {row.createdAt ? relativeTimeI18n(row.createdAt, t) : "—"}
        </span>
      </td>

      {/* Actions */}
      <td className="w-16 px-2 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-0.5">
          {!row.isSuperTenant && onOpenSettings && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onOpenSettings(row.id)}
                  aria-label="Organization settings"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Settings className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Settings</TooltipContent>
            </Tooltip>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100 data-[state=open]:opacity-100"
                aria-label="More actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem className="gap-2" onClick={() => navigator.clipboard.writeText(row.id)}>
                <Copy className="h-3.5 w-3.5" />
                Copy ID
              </DropdownMenuItem>
              {onOpenAuditLog && (
                <DropdownMenuItem className="gap-2" onClick={() => onOpenAuditLog(row.id, row.name)}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  View audit log
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

const OrgRowMemo = memo(OrgRowImpl);

// ─── OrgsTable ────────────────────────────────────────────────────────

interface OrgsTableProps {
  tenants: TenantListItem[];
  membersByTenant: Map<string, unknown[]>;
  groupsByTenant: Map<string, AccessGroupItem[]>;
  superTenantIds: Set<string>;
  sort: OrgSortState;
  onSort: (key: OrgSortKey) => void;
  activeId?: string | null;
  onRowClick?: (id: string) => void;
  onOpenSettings?: (tenantId: string) => void;
  onOpenAuditLog?: (tenantId: string, name: string) => void;
  flashId?: string | null;
  /** Vertical scroll container for row virtualization — owned by the dashboard. */
  scrollContainerRef?: RefObject<HTMLElement | null>;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
}

const TABLE_COLUMN_COUNT = 11;
const ROW_HEIGHT_ESTIMATE = 56;
const ROW_OVERSCAN = 8;

export function OrgsTable({
  tenants, membersByTenant, groupsByTenant, superTenantIds,
  sort, onSort, activeId, onRowClick, onOpenSettings, onOpenAuditLog,
  flashId, scrollContainerRef, hasMore = false, isFetchingNextPage = false,
}: OrgsTableProps) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);

  const deferredTenants = useDeferredValue(tenants);
  const rows = useMemo(
    () => deriveRows(deferredTenants, membersByTenant, groupsByTenant, superTenantIds),
    [deferredTenants, membersByTenant, groupsByTenant, superTenantIds],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: ROW_OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const topPad = virtualItems[0]?.start ?? 0;
  const bottomPad =
    virtualItems.length > 0
      ? totalSize - (virtualItems[virtualItems.length - 1]!.end)
      : 0;

  // Flash + scroll-into-view a freshly-jumped-to row. The row may not be in
  // the DOM yet (virtualized), so ask the virtualizer to scroll to its index
  // first, then apply the flash class on the next frame.
  useEffect(() => {
    if (!flashId) return;
    const idx = rows.findIndex((r) => r.id === flashId);
    if (idx < 0) return;
    virtualizer.scrollToIndex(idx, { align: "center" });
    const raf = requestAnimationFrame(() => {
      const row = tableRef.current?.querySelector<HTMLElement>(`[data-rowid="${flashId}"]`);
      if (!row) return;
      row.classList.remove("iam-row-flash");
      void row.offsetWidth;
      row.classList.add("iam-row-flash");
    });
    return () => {
      cancelAnimationFrame(raf);
      const row = tableRef.current?.querySelector<HTMLElement>(`[data-rowid="${flashId}"]`);
      row?.classList.remove("iam-row-flash");
    };
  }, [flashId, rows, virtualizer]);

  if (rows.length === 0 && !isFetchingNextPage) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-2 border-t border-border bg-card py-16 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{t("iam.orgsTable.emptyState.title", "No organizations match your filters")}</p>
        <p className="text-xs text-muted-foreground">{t("iam.orgsTable.emptyState.description", "Try clearing the filters or broadening your search.")}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col overflow-hidden border-t border-border bg-card">
      <style>{`@keyframes iam-row-flash{0%{background-color:color-mix(in oklch,var(--primary) 13%,transparent);}100%{background-color:transparent;}}.iam-row-flash{animation:iam-row-flash 1.4s ease-out forwards;}`}</style>
      <div className="flex-1 overflow-x-auto">
        <table ref={tableRef} className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="group border-b border-border bg-muted/30">
              <SortHeader id="name"      label={t("iam.orgsTable.organization", "Organization")} sort={sort} onSort={onSort} className="w-56" />
              <th className="hidden w-24 whitespace-nowrap px-3 py-2.5 text-left text-xs font-medium text-muted-foreground md:table-cell">{t("iam.orgsTable.type", "Type")}</th>
              <SortHeader id="members"   label={t("iam.orgsTable.members", "Members")}      sort={sort} onSort={onSort} className="w-24" />
              <th className="hidden w-20 px-3 py-2.5 text-left text-xs font-medium text-muted-foreground sm:table-cell">{t("iam.orgsTable.roles", "Roles")}</th>
              <th className="hidden w-24 px-3 py-2.5 text-left text-xs font-medium text-muted-foreground lg:table-cell">{t("iam.orgsTable.industries", "Industries")}</th>
              <th className="hidden w-20 px-3 py-2.5 text-left text-xs font-medium text-muted-foreground lg:table-cell">{t("iam.orgsTable.products", "Products")}</th>
              <th className="hidden w-32 px-3 py-2.5 text-left text-xs font-medium text-muted-foreground xl:table-cell">{t("iam.orgsTable.defaultBrand", "Default brand")}</th>
              <th className="hidden w-32 px-3 py-2.5 text-left text-xs font-medium text-muted-foreground xl:table-cell">{t("iam.orgsTable.defaultIndustry", "Default industry")}</th>
              <th className="hidden w-20 px-3 py-2.5 text-left text-xs font-medium text-muted-foreground lg:table-cell">{t("iam.orgsTable.retool", "Retool")}</th>
              <SortHeader id="createdAt" label={t("iam.orgsTable.created", "Created")}      sort={sort} onSort={onSort} className="hidden w-28 lg:table-cell" />
              <th className="w-16" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {topPad > 0 && (
              <tr aria-hidden style={{ height: topPad }}>
                <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
            {virtualItems.map((vi) => {
              const row = rows[vi.index];
              if (!row) return null;
              return (
                <OrgRowMemo
                  key={row.id}
                  row={row}
                  isActive={activeId === row.id}
                  onRowClick={onRowClick}
                  onOpenSettings={onOpenSettings}
                  onOpenAuditLog={onOpenAuditLog}
                />
              );
            })}
            {bottomPad > 0 && (
              <tr aria-hidden style={{ height: bottomPad }}>
                <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pager footer */}
      <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
        <span>
          Loaded <strong className="text-foreground">{rows.length}</strong> organizations
          {hasMore ? " (scroll for more)" : ""}
        </span>
        {isFetchingNextPage && <span className="text-muted-foreground">Loading more…</span>}
      </div>
    </div>
  );
}
